import {
  clockOffset,
  ServerMessageSchema,
  type ClientMessage,
  type ServerMessage,
} from '../meta/online/live';
import { apiUrl } from './online';

/**
 * A live room's connection (docs/LIVE.md, "How it works"): one WebSocket that reconnects by
 * itself, with a fresh ticket each time, until the room closes or the game lets go of it. It also
 * keeps the phone's clock offset from the server's, so both racers' countdowns end together.
 * Nothing here knows about races: it moves messages and says how the line is.
 */

export type LineState = 'connecting' | 'open' | 'reconnecting' | 'closed';

/** How a ticket request went: a ticket, try again later, or stop for good (with why). */
export type TicketAnswer = { ticket: string } | { retry: true } | { stop: string };

export interface LiveSocketOptions {
  /** The room's code. */
  code: string;
  /** Asks the server for a ticket (each connection needs a new one). */
  ticket: () => Promise<TicketAnswer>;
  onMessage: (message: ServerMessage) => void;
  /** The line's state; `closed` comes once, with why. */
  onLine: (state: LineState, why?: string) => void;
  /** For tests: makes the socket (the browser's WebSocket otherwise). */
  makeSocket?: (url: string) => WebSocketLike;
  /** For tests: the clock (Date.now otherwise). */
  now?: () => number;
}

/** The part of a WebSocket this uses. */
export interface WebSocketLike {
  readyState: number;
  send(data: string): void;
  close(code?: number, reason?: string): void;
  onopen: ((ev: unknown) => void) | null;
  onmessage: ((ev: { data: unknown }) => void) | null;
  onclose: ((ev: { code: number; reason: string }) => void) | null;
  onerror: ((ev: unknown) => void) | null;
}

const OPEN = 1;
/** Waits between reconnects: quick at first, then every few seconds. */
const BACKOFF_MS = [250, 500, 1000, 2000, 4000];
/** A ping every so often keeps an idle line alive (mobile networks drop quiet ones). */
const PING_EVERY_MS = 20_000;
/** Pings sent on each (re)connect, for the clock offset. */
const SYNC_PINGS = 3;
/** The room's close codes that mean "stop": closed, replaced by another device, not seated. */
const FINAL_CLOSE: Record<number, string> = {
  4000: 'This race is open on another device.',
  4001: 'The room closed.',
  4004: 'You are not in this room.',
};

/** The room socket's address, from the API's (https → wss). */
function socketUrl(code: string, ticket: string): string {
  const http = apiUrl(`/rooms/${code}/ws?ticket=${encodeURIComponent(ticket)}`);
  const absolute = http.startsWith('/') ? `${location.origin}${http}` : http;
  return absolute.replace(/^http/, 'ws');
}

export class LiveSocket {
  private socket: WebSocketLike | null = null;
  private stopped = false;
  private attempts = 0;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private samples: { sent: number; got: number; server: number }[] = [];
  private offsetMs = 0;
  private readonly now: () => number;

  constructor(private readonly options: LiveSocketOptions) {
    this.now = options.now ?? (() => Date.now());
    options.onLine('connecting');
    void this.connect();
  }

  /** The server's clock now, by the measured offset. */
  serverNow(): number {
    return this.now() + this.offsetMs;
  }

  /** A server time as this phone's clock reads it. */
  toLocal(serverMs: number): number {
    return serverMs - this.offsetMs;
  }

  /** Sends if the line is open; false when it is not (the message is dropped). */
  send(message: ClientMessage): boolean {
    if (!this.socket || this.socket.readyState !== OPEN) return false;
    this.socket.send(JSON.stringify(message));
    return true;
  }

  /** Lets go: closes the line and stops reconnecting. */
  close(): void {
    if (this.stopped) return;
    this.stop('left');
    this.socket?.close(1000, 'left');
  }

  /** For tests: closes the line as a bad connection would (it reconnects by itself). */
  drop(): void {
    this.socket?.close(3000, 'dropped');
    const socket = this.socket;
    if (socket) socket.onclose?.({ code: 3000, reason: 'dropped' });
  }

  /** Tries again at once (the device's connection came back). */
  retryNow(): void {
    if (this.stopped || (this.socket && this.socket.readyState === OPEN)) return;
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = null;
    void this.connect();
  }

  private async connect(): Promise<void> {
    if (this.stopped) return;
    const answer = await this.options.ticket();
    if (this.stopped) return;
    if ('stop' in answer) {
      this.stop(answer.stop);
      return;
    }
    if ('retry' in answer) {
      this.later();
      return;
    }
    const make =
      this.options.makeSocket ?? ((url: string) => new WebSocket(url) as unknown as WebSocketLike);
    const socket = make(socketUrl(this.options.code, answer.ticket));
    this.socket = socket;
    socket.onopen = () => {
      this.attempts = 0;
      this.options.onLine('open');
      this.samples = [];
      for (let i = 0; i < SYNC_PINGS; i++) setTimeout(() => this.ping(), i * 150);
      if (this.pingTimer) clearInterval(this.pingTimer);
      this.pingTimer = setInterval(() => this.ping(), PING_EVERY_MS);
    };
    socket.onmessage = (ev) => this.receive(ev.data);
    socket.onerror = () => {
      // A close follows; reconnecting starts there.
    };
    socket.onclose = (ev) => {
      if (this.socket !== socket) return;
      this.socket = null;
      if (this.pingTimer) clearInterval(this.pingTimer);
      this.pingTimer = null;
      if (this.stopped) return;
      const final = FINAL_CLOSE[ev.code];
      if (final) this.stop(final);
      else this.later();
    };
  }

  private later(): void {
    if (this.stopped) return;
    this.options.onLine('reconnecting');
    const wait = BACKOFF_MS[Math.min(this.attempts, BACKOFF_MS.length - 1)]!;
    this.attempts++;
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      void this.connect();
    }, wait);
  }

  private stop(why: string): void {
    this.stopped = true;
    if (this.retryTimer) clearTimeout(this.retryTimer);
    if (this.pingTimer) clearInterval(this.pingTimer);
    this.retryTimer = null;
    this.pingTimer = null;
    this.options.onLine('closed', why);
  }

  private ping(): void {
    this.send({ type: 'ping', at: this.now() });
  }

  private receive(data: unknown): void {
    if (typeof data !== 'string') return;
    let parsed;
    try {
      parsed = ServerMessageSchema.safeParse(JSON.parse(data));
    } catch {
      return;
    }
    if (!parsed.success) return;
    const message = parsed.data;
    if (message.type === 'pong') {
      this.samples = [
        ...this.samples,
        { sent: message.at, got: this.now(), server: message.server },
      ].slice(-8);
      this.offsetMs = clockOffset(this.samples);
      return;
    }
    this.options.onMessage(message);
  }
}
